import {
  AddressDto,
  CreateAddressForm,
  UpdateAddressForm,
} from '@act/shared/data-transfer-objects';
import { Control, FieldErrors, FormState } from 'react-hook-form';
import type { AddressForm } from '../types';
import { FormBuilder, SelectOption } from '@act/storybook';
import { Box } from '@mui/material';
import STATE_OPTIONS from 'redux/constants/stateOptions';
import { FormConfig } from '../../../stories/forms/FormBuilder/FormBuilder.types';

interface AddressContentProps {
  control: Control<CreateAddressForm | UpdateAddressForm>;
  errors: FieldErrors<CreateAddressForm | UpdateAddressForm>;
  addresses?: AddressDto[];
  selectedAddress?: AddressDto;
  isEdit?: boolean;
  showDetails?: boolean;
}

export const AddressContent = ({ control, errors }: AddressContentProps) => {
  const stateOptions = STATE_OPTIONS;

  const formConfig: FormConfig<CreateAddressForm | UpdateAddressForm> = [
    {
      type: 'input',
      name: 'description',
      label: 'Label',
      input: 'text',
      props: {
        placeholder: 'e.g., Home, Work, etc.',
        characterLimit: 20,
        required: true,
      },
    },
    {
      type: 'input',
      name: 'line1',
      label: 'Street Address',
      input: 'text',
      props: {
        required: true,
      },
    },
    {
      type: 'input',
      name: 'line2',
      label: 'Apartment, suite, etc.',
      input: 'text',
    },
    {
      type: 'input',
      name: 'city',
      label: 'City',
      input: 'text',
      props: {
        required: true,
      },
    },
    [
      {
        type: 'input',
        name: 'state',
        label: 'State',
        input: 'select',
        props: {
          options: Object.keys(stateOptions).map(
            key =>
              ({
                key: key,
                label: stateOptions[key],
                value: stateOptions[key],
              } as SelectOption<string>)
          ),
          required: true,
        },
      },
      {
        type: 'input',
        name: 'zip',
        label: 'ZIP Code',
        input: 'text',
        props: {
          required: true,
        },
      },
    ],
  ];

  const formState: FormState<CreateAddressForm | UpdateAddressForm> = {
    errors,
    isDirty: false,
    isLoading: false,
    isSubmitted: false,
    isSubmitSuccessful: false,
    isSubmitting: false,
    isValid: true,
    isValidating: false,
    submitCount: 0,
    touchedFields: {},
    dirtyFields: {},
    disabled: false,
    validatingFields: {},
    isReady: false,
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <FormBuilder
        config={formConfig}
        control={control}
        formState={formState}
      />
    </Box>
  );
};

export const EditAddressContent = ({
  control,
  errors,
  addresses,
  selectedAddress,
  isEdit,
  showDetails,
}: AddressContentProps) => {
  function getDisplayAddress(address: AddressDto): string {
    const { description, line1 } = address;
    return description ? description : line1;
  }
  const formConfig: FormConfig<CreateAddressForm | UpdateAddressForm> = [
    {
      type: 'input',
      name: 'addressId',
      label: 'Select Address',
      input: 'select',
      props: {
        options:
          addresses?.map(
            address =>
              ({
                key: address.id,
                label: getDisplayAddress(address),
                value: address.id,
              } as SelectOption<number>)
          ) || [],
        fullWidth: true,
      },
    },
  ];
  if (isEdit) {
    formConfig.push({
      type: 'input',
      name: 'description',
      label: 'Label',
      input: 'text',
      disabled: !selectedAddress,
      props: {
        characterLimit: 20,
        required: true,
        placeholder: 'e.g., Home, Work, etc.',
      },
    });
  }
  if (isEdit || showDetails) {
    formConfig.push(
      {
        type: 'group',
        items: [
          {
            type: 'input',
            name: 'line1',
            label: 'Street Address',
            input: 'text',
            disabled: showDetails || !selectedAddress,
            props: {
              required: true,
            },
          },
          {
            type: 'input',
            name: 'line2',
            label: 'Apartment, suite, etc.',
            input: 'text',
            disabled: showDetails || !selectedAddress,
          },
        ],
      },
      {
        type: 'group',
        items: [
          {
            type: 'input',
            name: 'city',
            label: 'City',
            input: 'text',
            disabled: showDetails || !selectedAddress,
            props: {
              required: true,
            },
          },
          {
            type: 'input',
            name: 'state',
            label: 'State',
            input: 'text',
            disabled: showDetails || !selectedAddress,
            props: {
              required: true,
            },
          },
          {
            type: 'input',
            name: 'zip',
            label: 'ZIP Code',
            input: 'text',
            disabled: showDetails || !selectedAddress,
            props: {
              required: true,
            },
          },
        ],
      }
    );
  }

  const formState: FormState<AddressForm> = {
    errors,
    isDirty: false,
    isLoading: false,
    isSubmitted: false,
    isSubmitSuccessful: false,
    isSubmitting: false,
    isValid: true,
    isValidating: false,
    submitCount: 0,
    touchedFields: {},
    dirtyFields: {},
    disabled: false,
    validatingFields: {},
    isReady: false,
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <FormBuilder
        config={formConfig}
        control={control}
        formState={formState}
      />
    </Box>
  );
};
